<!-- src/lib/components/RulesView.svelte -->
<script lang="ts">
  import { onMount } from 'svelte';
  import { api } from '../api.ts';

  interface Props {
    workspaceId: string;
  }
  let { workspaceId }: Props = $props();

  interface Rule {
    id:            string;
    workspace_id:  string;
    name:          string;
    trigger:       string;
    action:        string;
    action_params: Record<string, unknown>;
    enabled:       boolean;
    created_at:    string;
  }

  let rules    = $state<Rule[]>([]);
  let loading  = $state(true);
  let creating = $state(false);
  let error    = $state<string | null>(null);

  let newName        = $state('');
  let newTrigger     = $state('card_created');
  let newAction      = $state('assign_agent');
  let newActionParam = $state('');
  let showForm       = $state(false);

  const triggerLabels: Record<string, string> = {
    card_created:              'A card is created',
    priority_changed_to_urgent:'Priority changes to urgent',
    card_moved_to_done:        'Card moved to Done',
  };

  const actionLabels: Record<string, string> = {
    assign_agent:  'Start an agent',
    set_priority:  'Set priority',
    add_label:     'Add label',
  };

  async function loadRules() {
    loading = true;
    error   = null;
    try {
      rules = await api<Rule[]>(`/api/v1/workspaces/${workspaceId}/rules`);
    } catch (e) {
      error = String(e);
    } finally {
      loading = false;
    }
  }

  async function createRule() {
    if (!newName.trim()) return;
    creating = true;
    error    = null;
    try {
      let action_params: Record<string, unknown> = {};
      if (newAction === 'set_priority' && newActionParam) {
        action_params = { value: newActionParam };
      } else if (newAction === 'add_label' && newActionParam) {
        action_params = { label_id: newActionParam };
      }
      const rule = await api<Rule>(`/api/v1/workspaces/${workspaceId}/rules`, {
        method: 'POST',
        body: JSON.stringify({
          name:          newName.trim(),
          trigger:       newTrigger,
          action:        newAction,
          action_params,
        }),
      });
      rules = [...rules, rule];
      newName        = '';
      newTrigger     = 'card_created';
      newAction      = 'assign_agent';
      newActionParam = '';
      showForm       = false;
    } catch (e) {
      error = String(e);
    } finally {
      creating = false;
    }
  }

  async function toggleRule(rule: Rule) {
    try {
      const updated = await api<Rule>(`/api/v1/rules/${rule.id}/toggle`, {
        method: 'PATCH',
        body: JSON.stringify({ enabled: !rule.enabled }),
      });
      rules = rules.map(r => r.id === updated.id ? updated : r);
    } catch (e) {
      error = String(e);
    }
  }

  async function deleteRule(rule: Rule) {
    if (!confirm(`Delete rule "${rule.name}"?`)) return;
    try {
      await api(`/api/v1/rules/${rule.id}`, { method: 'DELETE' });
      rules = rules.filter(r => r.id !== rule.id);
    } catch (e) {
      error = String(e);
    }
  }

  onMount(loadRules);
</script>

<div class="rules-view">
  <div class="rules-header">
    <div class="rules-title-row">
      <h2 class="rules-title">Automation Rules</h2>
      <span class="rules-count">{rules.length}</span>
    </div>
    <p class="rules-sub">Trigger automatic actions when board events occur.</p>
    <button class="new-rule-btn" onclick={() => { showForm = !showForm; }}>
      {showForm ? 'Cancel' : '+ New Rule'}
    </button>
  </div>

  {#if error}
    <div class="error-banner">{error}</div>
  {/if}

  {#if showForm}
    <form class="rule-form" onsubmit={(e) => { e.preventDefault(); createRule(); }}>
      <div class="form-row">
        <label class="form-label" for="rule-name-input">Rule name</label>
        <input
          id="rule-name-input"
          class="form-input"
          bind:value={newName}
          placeholder="e.g. Auto-assign agent on urgent"
          required
        />
      </div>
      <div class="form-row when-then">
        <div class="form-group">
          <label class="form-label" for="rule-trigger-select">When</label>
          <select id="rule-trigger-select" class="form-select" bind:value={newTrigger}>
            <option value="card_created">A card is created</option>
            <option value="priority_changed_to_urgent">Priority changes to urgent</option>
            <option value="card_moved_to_done">Card moved to Done</option>
          </select>
        </div>
        <span class="arrow">→</span>
        <div class="form-group">
          <label class="form-label" for="rule-action-select">Then</label>
          <select id="rule-action-select" class="form-select" bind:value={newAction}>
            <option value="assign_agent">Start an agent</option>
            <option value="set_priority">Set priority</option>
            <option value="add_label">Add label</option>
          </select>
        </div>
      </div>

      {#if newAction === 'set_priority'}
        <div class="form-row">
          <label class="form-label" for="rule-priority-select">Priority value</label>
          <select id="rule-priority-select" class="form-select" bind:value={newActionParam}>
            <option value="urgent">Urgent</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
            <option value="none">None</option>
          </select>
        </div>
      {:else if newAction === 'add_label'}
        <div class="form-row">
          <label class="form-label" for="rule-label-input">Label ID</label>
          <input
            id="rule-label-input"
            class="form-input"
            bind:value={newActionParam}
            placeholder="Label ID"
          />
        </div>
      {/if}

      <div class="form-actions">
        <button type="submit" class="btn-primary" disabled={creating || !newName.trim()}>
          {creating ? 'Creating…' : 'Create Rule'}
        </button>
      </div>
    </form>
  {/if}

  {#if loading}
    <div class="loading">Loading rules…</div>
  {:else if rules.length === 0}
    <div class="empty-rules">
      <span class="empty-icon">⚡</span>
      <p>No rules yet. Create one to automate your workflow.</p>
    </div>
  {:else}
    <div class="rules-list">
      {#each rules as rule (rule.id)}
        <div class="rule-card" class:disabled={!rule.enabled}>
          <div class="rule-left">
            <button
              class="toggle-btn"
              class:on={rule.enabled}
              onclick={() => toggleRule(rule)}
              title={rule.enabled ? 'Disable rule' : 'Enable rule'}
            >
              <span class="toggle-knob"></span>
            </button>
            <div class="rule-info">
              <span class="rule-name">{rule.name}</span>
              <div class="rule-desc">
                <span class="trigger-chip">{triggerLabels[rule.trigger] ?? rule.trigger}</span>
                <span class="arrow-sm">→</span>
                <span class="action-chip">{actionLabels[rule.action] ?? rule.action}</span>
              </div>
            </div>
          </div>
          <button
            class="delete-rule-btn"
            onclick={() => deleteRule(rule)}
            title="Delete rule"
          >✕</button>
        </div>
      {/each}
    </div>
  {/if}
</div>

<style>
  .rules-view {
    flex: 1;
    overflow-y: auto;
    padding: 1.75rem 2rem;
    max-width: 800px;
    width: 100%;
    margin: 0 auto;
    box-sizing: border-box;
  }

  .rules-header {
    margin-bottom: 1.5rem;
  }

  .rules-title-row {
    display: flex;
    align-items: center;
    gap: 0.625rem;
    margin-bottom: 0.35rem;
  }

  .rules-title {
    font-size: 1.1rem;
    font-weight: 600;
    color: var(--text-primary, #e8eaf0);
    margin: 0;
  }

  .rules-count {
    font-size: 0.68rem;
    font-family: 'JetBrains Mono', monospace;
    background: rgba(255,255,255,0.08);
    color: rgba(255,255,255,0.4);
    border-radius: 8px;
    padding: 0.05rem 0.45rem;
  }

  .rules-sub {
    font-size: 0.8rem;
    color: var(--text-muted, rgba(255,255,255,0.35));
    margin: 0 0 0.875rem;
  }

  .new-rule-btn {
    background: oklch(0.72 0.28 310 / 0.10);
    border: 1px solid oklch(0.72 0.28 310 / 0.26);
    color: oklch(0.74 0.28 310 / 0.84);
    padding: 0.35rem 0.875rem;
    border-radius: 6px;
    cursor: pointer;
    font-size: 0.8rem;
    font-weight: 500;
    transition: background-color 100ms ease, color 100ms ease, border-color 100ms ease;
  }
  .new-rule-btn:hover {
    background: oklch(0.72 0.28 310 / 0.17);
    border-color: oklch(0.72 0.28 310 / 0.42);
    color: oklch(0.80 0.26 310);
  }

  .error-banner {
    background: oklch(0.62 0.26 25 / 0.08);
    border: 1px solid oklch(0.62 0.26 25 / 0.20);
    color: rgba(255,80,80,0.8);
    border-radius: 6px;
    padding: 0.6rem 0.875rem;
    font-size: 0.8rem;
    margin-bottom: 1rem;
  }

  /* New rule form */
  .rule-form {
    background: rgba(255,255,255,0.02);
    border: 1px solid rgba(255,255,255,0.07);
    border-radius: 8px;
    padding: 1rem 1.125rem;
    margin-bottom: 1.25rem;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .form-row {
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
  }

  .when-then {
    flex-direction: row;
    align-items: flex-end;
    gap: 0.75rem;
  }

  .form-group {
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
    flex: 1;
  }

  .form-label {
    font-size: 0.68rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: rgba(255,255,255,0.3);
  }

  .form-input,
  .form-select {
    background: rgba(255,255,255,0.04);
    border: 1px solid rgba(255,255,255,0.1);
    color: var(--text-primary, #e8eaf0);
    padding: 0.4rem 0.65rem;
    border-radius: 5px;
    font-size: 0.82rem;
    outline: none;
    transition: border-color 150ms ease;
    width: 100%;
    box-sizing: border-box;
    font-family: var(--font-sans, system-ui, sans-serif);
  }
  .form-input:focus,
  .form-select:focus {
    border-color: oklch(0.72 0.28 310 / 0.36);
  }
  .form-select option {
    background: #1a1c24;
  }

  .arrow {
    color: rgba(255,255,255,0.2);
    font-size: 1rem;
    padding-bottom: 0.4rem;
    flex-shrink: 0;
  }

  .form-actions {
    display: flex;
    justify-content: flex-end;
  }

  .btn-primary {
    background: oklch(0.72 0.28 310 / 0.12);
    border: 1px solid oklch(0.72 0.28 310 / 0.32);
    color: oklch(0.80 0.26 310);
    padding: 0.4rem 1rem;
    border-radius: 6px;
    cursor: pointer;
    font-size: 0.8rem;
    font-weight: 500;
    transition: background-color 100ms ease, color 100ms ease, border-color 100ms ease;
  }
  .btn-primary:hover:not(:disabled) {
    background: oklch(0.72 0.28 310 / 0.22);
    border-color: oklch(0.74 0.28 310 / 0.46);
  }
  .btn-primary:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  /* Loading / empty */
  .loading {
    text-align: center;
    color: rgba(255,255,255,0.2);
    font-size: 0.875rem;
    padding: 2rem 0;
  }

  .empty-rules {
    text-align: center;
    padding: 3rem 0;
    color: rgba(255,255,255,0.2);
  }
  .empty-icon {
    font-size: 2rem;
    display: block;
    margin-bottom: 0.75rem;
    opacity: 0.4;
  }
  .empty-rules p {
    font-size: 0.875rem;
    margin: 0;
  }

  /* Rules list */
  .rules-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .rule-card {
    background: rgba(255,255,255,0.025);
    border: 1px solid rgba(255,255,255,0.07);
    border-radius: 8px;
    padding: 0.75rem 1rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    transition: background 150ms ease, border-color 150ms ease;
  }
  .rule-card:hover {
    background: rgba(255,255,255,0.04);
    border-color: rgba(255,255,255,0.12);
  }
  .rule-card.disabled {
    opacity: 0.45;
  }

  .rule-left {
    display: flex;
    align-items: center;
    gap: 0.875rem;
    flex: 1;
    min-width: 0;
  }

  /* Toggle switch */
  .toggle-btn {
    position: relative;
    width: 32px;
    height: 18px;
    background: rgba(255,255,255,0.08);
    border: 1px solid rgba(255,255,255,0.12);
    border-radius: 9px;
    cursor: pointer;
    transition: background 150ms ease, border-color 150ms ease;
    flex-shrink: 0;
    padding: 0;
  }
  .toggle-btn.on {
    background: oklch(0.72 0.28 310 / 0.26);
    border-color: oklch(0.72 0.28 310 / 0.42);
  }
  .toggle-knob {
    position: absolute;
    top: 2px;
    left: 2px;
    width: 12px;
    height: 12px;
    background: rgba(255,255,255,0.3);
    border-radius: 50%;
    transition: transform 150ms ease, background 150ms ease;
  }
  .toggle-btn.on .toggle-knob {
    transform: translateX(14px);
    background: oklch(0.80 0.26 310);
  }

  .rule-info {
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
    min-width: 0;
  }

  .rule-name {
    font-size: 0.85rem;
    color: var(--text-primary, #e8eaf0);
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .rule-desc {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    flex-wrap: wrap;
  }

  .trigger-chip,
  .action-chip {
    font-size: 0.68rem;
    border-radius: 3px;
    padding: 2px 6px;
    line-height: 1.3;
  }
  .trigger-chip {
    background: oklch(0.55 0.20 265 / 0.14);
    border: 1px solid oklch(0.55 0.20 265 / 0.28);
    color: oklch(0.55 0.20 265 / 0.80);
  }
  .action-chip {
    background: oklch(0.72 0.28 310 / 0.10);
    border: 1px solid oklch(0.72 0.28 310 / 0.22);
    color: oklch(0.82 0.26 310 / 0.78);
  }

  .arrow-sm {
    color: rgba(255,255,255,0.2);
    font-size: 0.75rem;
  }

  .delete-rule-btn {
    background: none;
    border: none;
    color: rgba(255,255,255,0.15);
    cursor: pointer;
    font-size: 0.75rem;
    padding: 0.25rem 0.4rem;
    border-radius: 4px;
    transition: background-color 100ms ease, color 100ms ease, border-color 100ms ease;
    flex-shrink: 0;
  }
  .delete-rule-btn:hover {
    color: rgba(255,80,80,0.7);
    background: rgba(255,80,80,0.08);
  }
</style>
